import "./index.scss";
import React, { useState, useEffect } from "react";
import { Alert, Modal } from "react-bootstrap";
import { v2 } from "../../services/axios";

const MAX_COLLABORATORS = 10;

function CollaboratorRow({ collaborator, owners, onUpdate, onRemove }) {
  const [role, setRole] = useState(collaborator.role);

  useEffect(() => {
    setRole(collaborator.role);
  }, [collaborator.role]);

  const isOnlyOwner = collaborator.role === "OWNER" && owners.length === 1;

  const date = collaborator.inviteExpires
    ? new Date(collaborator.inviteExpires)
    : null;
  const daysLeft = date
    ? Math.max(
        0,
        Math.round((date.getTime() - Date.now()) / (1000 * 3600 * 24)),
      )
    : null;

  function handleRoleChange(e) {
    const newRole = e.target.value;
    setRole(newRole);
    onUpdate(collaborator.email, newRole);
  }

  return (
    <div className="row md-manage-shares-wrapper align-items-center">
      <div className="col-5">
        <div className="collaborator-email">
          <span className="email-text">{collaborator.email}</span>
          {collaborator.inviteExpires && (
            <small className="invite-pending">
              <span className="reshare-link">Reshare</span> &mdash; Pending (
              {daysLeft} days)
            </small>
          )}
        </div>
      </div>
      <div className="col-5">
        {isOnlyOwner ? (
          <span className="owner-label">OWNER</span>
        ) : (
          <select value={role} onChange={handleRoleChange}>
            <option value="OWNER">Owner</option>
            <option value="CONTRIBUTOR">Contributor</option>
            <option value="VIEWER">Viewer</option>
          </select>
        )}
      </div>
      <div className="col-2 actions-wrapper">
        {!isOnlyOwner && (
          <button
            className="btn-remove-icon"
            onClick={() => onRemove(collaborator.email)}
            title="Remove collaborator"
          >
            <i className="fa fa-trash" aria-hidden="true" />
          </button>
        )}
      </div>
    </div>
  );
}

function SharingModal({ reportID, hideModal, address }) {
  const [collaborators, setCollaborators] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newEmail, setNewEmail] = useState("");
  const [newRole, setNewRole] = useState("OWNER");
  const [alert, setAlert] = useState(null);

  async function fetchCollaborators() {
    try {
      setLoading(true);
      const { data } = await v2.get(`/domoreports/${reportID}/collaborators/`);
      setCollaborators(data);
    } catch {
      setAlert({ variant: "danger", message: "Failed to load collaborators." });
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    fetchCollaborators();
  }, [reportID]);

  async function addCollaborator() {
    if (!newEmail) {
      setAlert({
        variant: "danger",
        message: "Please enter a valid email address.",
      });
      return;
    }
    try {
      await v2.post(`/domoreports/${reportID}/collaborators/`, {
        email: newEmail,
        role: newRole,
      });
      setAlert({
        variant: "success",
        message: `${newEmail} has been given ${newRole} access.`,
      });
      setNewEmail("");
      setNewRole("OWNER");
      await fetchCollaborators();
    } catch (error) {
      setAlert({
        variant: "danger",
        message:
          error?.response?.data?.message || "Failed to add collaborator.",
      });
    }
  }

  async function updateCollaborator(email, role) {
    try {
      await v2.patch(`/domoreports/${reportID}/collaborators/`, {
        email,
        role,
      });
      setAlert({
        variant: "success",
        message: `${email} has been given ${role} access.`,
      });
      await fetchCollaborators();
    } catch (error) {
      setAlert({
        variant: "danger",
        message:
          error?.response?.data?.message || "Failed to update collaborator.",
      });
      await fetchCollaborators();
    }
  }

  async function removeCollaborator(email) {
    try {
      await v2.delete(
        `/domoreports/${reportID}/collaborators?email=${encodeURIComponent(email)}`,
        {
          headers: { "Content-Type": "application/json" },
          data: { email },
        },
      );
      setAlert({ variant: "warning", message: "User removed." });
      await fetchCollaborators();
    } catch {
      setAlert({
        variant: "danger",
        message: "Failed to remove collaborator.",
      });
    }
  }

  const owners = collaborators.filter((c) => c.role === "OWNER");
  const remainingSlots = MAX_COLLABORATORS - collaborators.length;

  return (
    <Modal
      size="lg"
      aria-labelledby="sharing-modal-title"
      centered
      show={true}
      onHide={hideModal}
      className="md-sharing-modal"
    >
      <Modal.Header closeButton>
        <Modal.Title id="sharing-modal-title">Sharing</Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {alert && (
          <Alert
            variant={alert.variant}
            onClose={() => setAlert(null)}
            dismissible
          >
            {alert.message}
          </Alert>
        )}

        <p className="modal-address">{address}</p>

        {!loading && (
          <p className="sharing-subtitle">
            You may share your Home Repair Estimate with up to {remainingSlots}{" "}
            additional users.
          </p>
        )}

        {loading ? (
          <div className="md-spinner-container text-center">
            <div className="spinner-border text-primary" role="status">
              <span className="sr-only">Loading...</span>
            </div>
          </div>
        ) : (
          <>
            <div className="row sharing-header">
              <div className="col-5">
                <strong>Email</strong>
              </div>
              <div className="col-5">
                <strong>Access</strong>
              </div>
              <div className="col-2" />
            </div>

            {collaborators.map((collaborator) => (
              <CollaboratorRow
                key={collaborator.email}
                collaborator={collaborator}
                owners={owners}
                onUpdate={updateCollaborator}
                onRemove={removeCollaborator}
              />
            ))}

            {collaborators.length < MAX_COLLABORATORS && (
              <div className="row md-manage-shares-wrapper new-collaborator-row align-items-center">
                <div className="col-5">
                  <input
                    type="email"
                    placeholder="firstlast@example.com"
                    value={newEmail}
                    onChange={(e) => setNewEmail(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && addCollaborator()}
                  />
                </div>
                <div className="col-5">
                  <select
                    value={newRole}
                    onChange={(e) => setNewRole(e.target.value)}
                  >
                    <option value="OWNER">Owner</option>
                    <option value="CONTRIBUTOR">Contributor</option>
                    <option value="VIEWER">Viewer</option>
                  </select>
                </div>
                <div className="col-2 actions-wrapper">
                  <button
                    className={`btn-confirm${newEmail ? "" : " disabled"}`}
                    onClick={addCollaborator}
                    disabled={!newEmail}
                    title="Add collaborator"
                  >
                    <i className="fa fa-check-circle" aria-hidden="true" />
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </Modal.Body>

      <Modal.Footer>
        <button className="btn btn-tertiary" onClick={hideModal}>
          Done
        </button>
      </Modal.Footer>
    </Modal>
  );
}

export default SharingModal;
